import { create, type StoreApi, type UseBoundStore } from "zustand";
import { TurnstileTimeoutError } from "../lib/awaitTokenError";

export type TurnstileWidgetState =
  | "idle"
  | "pending"
  | "ready"
  | "expired"
  | "error"
  | "interaction-required";

interface PendingResolver {
  promise: Promise<string>;
  resolve: (token: string) => void;
  reject: (error: Error) => void;
  timer: number;
}

interface TurnstileStore {
  /**
   * 直近の Turnstile challenge で得た token。consumeToken で消費 / clearToken
   * で破棄するまで保持される。state==='ready' のときだけ有効。
   */
  token: string | null;
  issuedAt: number | null;
  /**
   * バックエンドの「post 成功後 30 分は token 不要」セッションの失効時刻 (epoch ms)。
   * iOS のみ post / thread create 成功時に startSession() で「now + 30 分」が
   * セットされ (既に値があれば変えない)、reply submit 時はこれが現時刻より
   * 1 分以上未来なら challenge / token なしで送信できる。
   *
   * 重要: token の生存期間とは独立した概念。CF 側 token が 5 分で失効しても
   * このフィールドは影響を受けず、submit は session で通る。
   */
  expireTime_tokenSession: number | null;
  /**
   * Turnstile widget の純粋な CF 状態。session とは独立。
   */
  state: TurnstileWidgetState;
  visible: boolean;

  /** Provider が登録する評価関数。各 action の最後に呼ばれる */
  _evaluate: (() => void) | null;
  /** awaitToken の保留中 Promise。setToken or unmount で resolve/reject */
  _pending: PendingResolver | null;

  setState: (s: TurnstileWidgetState) => void;
  setToken: (t: string) => void;
  /**
   * token / issuedAt をクリアする (state も session も触らない)。expired /
   * refresh 経路で widget を reset した直後に呼んで、awaitToken が古い
   * トークンを返さないようにする
   */
  clearToken: () => void;
  consumeToken: () => string | null;
  /**
   * バックエンドセッションを開始する (iOS の post / thread create 成功時)。
   * expireTime_tokenSession が null のときだけ「now + 30 分」をセットし、
   * 既に値が入っていれば変えない (session は最初の post から実 30 分で
   * 必ず終了させる; 累積延長を防ぐ)。
   */
  startSession: () => void;
  /**
   * バックエンドセッションを終了 (expireTime_tokenSession を null に)。
   * iOS のスレ立てモーダル open 時 / 1 分監視タイマーの失効目前で呼ばれる。
   */
  clearSession: () => void;
  requestRefresh: () => void;
  setVisible: (v: boolean) => void;
  registerEvaluator: (evaluate: () => void) => () => void;
  awaitToken: (timeoutMs?: number) => Promise<string>;
  /** Provider unmount 時の保留 Promise クリア */
  _rejectPending: (error: Error) => void;
  /** requestRefresh によって reset を要求されたかのフラグ */
  _refreshRequested: boolean;
  _consumeRefreshRequest: () => boolean;
}

function evaluate(get: () => TurnstileStore): void {
  get()._evaluate?.();
}

/** バックエンドセッションの長さ (バックエンド仕様の 30 分セッションに合わせる) */
const SESSION_DURATION_MS: number = 30 * 60 * 1000;

export const useTurnstileStore: UseBoundStore<StoreApi<TurnstileStore>> =
  create<TurnstileStore>((set, get) => ({
    token: null,
    issuedAt: null,
    expireTime_tokenSession: null,
    state: "idle",
    visible: false,
    _evaluate: null,
    _pending: null,
    _refreshRequested: false,

    setState: (s: TurnstileWidgetState): void => {
      if (get().state === s) return;
      set({ state: s });
      evaluate(get);
    },

    setToken: (token: string): void => {
      // CF challenge で取れた新規 token。session とは独立なので
      // expireTime_tokenSession には触らない。
      set({ token, issuedAt: Date.now(), state: "ready" });
      const pending = get()._pending;
      if (pending) {
        window.clearTimeout(pending.timer);
        pending.resolve(token);
        set({ _pending: null });
      }
      evaluate(get);
    },

    clearToken: (): void => {
      const { token, issuedAt } = get();
      if (!token && issuedAt === null) return;
      set({ token: null, issuedAt: null });
      // state は呼び出し元で別途管理する (awaitToken 待機者には影響を与えない)
      // session も触らない
    },

    consumeToken: (): string | null => {
      const { token } = get();
      if (!token) return null;
      // === state を直接 idle にしない設計の理由 ===
      // この関数は token を破棄するだけでなく widget の visual reset
      // (turnstile.reset()) も伴う必要がある。reset() は Provider 側が
      // widget ID を握っているので、ここから直接呼べない。
      //
      // そこで「state は ready のまま」「_refreshRequested を立てる」
      // 「evaluate を発火させる」の 3 点だけを行い、残りの処理を Provider の
      // evaluator に委ねる。evaluator の refresh handler は state===ready の
      // ときだけ turnstile.reset() を呼ぶ仕様なので、state を先に idle に
      // 変えてしまうと reset() が走らず widget が前回の ✅ 表示のまま固着する。
      //
      // 実際の state 遷移シーケンス:
      //   1. ここで _refreshRequested=true (state は ready のまま)
      //   2. evaluator: refresh handler が turnstile.reset() を実行
      //   3. evaluator: clearToken (このメソッドで既に null 化済み, no-op)
      //   4. evaluator: setState("idle") → 再帰 evaluate
      //   5. 再帰 evaluator: idle case で execute をスケジュール (非 iOS のみ;
      //      iOS は iosBlocksAuto で停止し focusout/submit 待ち)
      //
      // session (expireTime_tokenSession) には一切触らない。session 開始は
      // form 側で startSession() を呼んで明示的に行う。
      set({
        token: null,
        issuedAt: null,
        _refreshRequested: true,
      });
      evaluate(get);
      return token;
    },

    startSession: (): void => {
      const { expireTime_tokenSession } = get();
      // 既存セッションがあれば延長しない (累積延長で実 30 分セッションを
      // 跨いで stale 状態になるのを防ぐ)
      if (expireTime_tokenSession !== null) return;
      set({ expireTime_tokenSession: Date.now() + SESSION_DURATION_MS });
    },

    clearSession: (): void => {
      if (get().expireTime_tokenSession === null) return;
      set({ expireTime_tokenSession: null });
    },

    requestRefresh: (): void => {
      set({ _refreshRequested: true });
      evaluate(get);
    },

    _consumeRefreshRequest: (): boolean => {
      const requested = get()._refreshRequested;
      if (requested) set({ _refreshRequested: false });
      return requested;
    },

    setVisible: (v: boolean): void => {
      if (get().visible === v) return;
      set({ visible: v });
      evaluate(get);
    },

    registerEvaluator: (fn: () => void): (() => void) => {
      set({ _evaluate: fn });
      // 登録直後にも評価を走らせる
      fn();
      return (): void => {
        if (get()._evaluate === fn) {
          set({ _evaluate: null });
        }
      };
    },

    awaitToken: (timeoutMs: number = 30_000): Promise<string> => {
      // state === 'ready' を要件に含める。expired/error などで token フィールドが
      // 残っているケースでも誤って古い token を返さないようにする
      const { token, state } = get();
      if (token && state === "ready") return Promise.resolve(token);

      const pending = get()._pending;
      if (pending) return pending.promise;

      let resolveFn!: (token: string) => void;
      let rejectFn!: (error: Error) => void;
      const promise = new Promise<string>((resolve, reject) => {
        resolveFn = resolve;
        rejectFn = reject;
      });
      const timer = window.setTimeout(() => {
        const cur = get()._pending;
        if (cur && cur.promise === promise) {
          set({ _pending: null });
          rejectFn(new TurnstileTimeoutError());
        }
      }, timeoutMs);
      set({
        _pending: { promise, resolve: resolveFn, reject: rejectFn, timer },
      });
      // 待機開始を Provider に伝える
      evaluate(get);
      return promise;
    },

    _rejectPending: (error: Error): void => {
      const pending = get()._pending;
      if (!pending) return;
      window.clearTimeout(pending.timer);
      pending.reject(error);
      set({ _pending: null });
    },
  }));
